import XLSX from "xlsx-js-style";

const border = {
  top: { style: "thin", color: { rgb: "FFBFBFBF" } },
  right: { style: "thin", color: { rgb: "FFBFBFBF" } },
  bottom: { style: "thin", color: { rgb: "FFBFBFBF" } },
  left: { style: "thin", color: { rgb: "FFBFBFBF" } },
};

const sHeader = {
  font: { bold: true, sz: 12 },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  fill: { patternType: "solid", fgColor: { rgb: "FFF2F2F2" } },
  border,
};

const baseCell = {
  alignment: { vertical: "center", wrapText: true },
  border,
};

const fill = (rgb) => ({
  fill: { patternType: "solid", fgColor: { rgb } },
});

const ddmmyyyy = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const calcStatus = (record) => {
  if (record?.isIrrelevant) return { label: "Неактуален", color: "default" };
  const validity = record?.validityPeriod ? new Date(record.validityPeriod) : null;
  if (!validity || Number.isNaN(validity.getTime()))
    return { label: "Не задано", color: "default" };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  validity.setHours(0, 0, 0, 0);

  if (validity < today) return { label: "Просрочен", color: "error" };
  return { label: "Действует", color: "success" };
};

const statusFill = (statusColor) => {
  switch (statusColor) {
    case "success":
      return fill("FFE6F4EA");
    case "error":
      return fill("FFFDE7E9");
    default:
      return fill("FFF5F5F5");
  }
};

const autoWidth = (ws) => {
  const ref = ws["!ref"];
  if (!ref) return;
  const range = XLSX.utils.decode_range(ref);
  const widths = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    let max = 10;
    for (let r = range.s.r; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      const text = cell?.v == null ? "" : String(cell.v);
      const len = Math.min(80, Math.ceil(text.length * 1.1) + 2);
      if (len > max) max = len;
    }
    widths.push(max);
  }
  ws["!cols"] = widths.map((wch) => ({ wch }));
};

export function exportAccreditationIndicatorExcel(rows, ctx = {}) {
  const formatAddress = (address) => {
    if (!address) return "";
    if (Array.isArray(address)) return address.filter(Boolean).join(", ");
    if (typeof address === "string") {
      const trimmed = address.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
        try {
          return formatAddress(JSON.parse(trimmed));
        } catch (e) {
          // fall through
        }
      }
      return address;
    }
    if (typeof address === "object") {
      const entries = Object.entries(address);
      if (!entries.length) return "";
      return entries
        .map(([k, v]) => {
          if (v == null) return null;
          if (
            typeof v === "string" ||
            typeof v === "number" ||
            typeof v === "boolean"
          ) {
            return `${k}: ${String(v)}`;
          }
          try {
            return `${k}: ${JSON.stringify(v)}`;
          } catch (e) {
            return `${k}: ${String(v)}`;
          }
        })
        .filter(Boolean)
        .join(", ");
    }
    try {
      return JSON.stringify(address);
    } catch (e) {
      return String(address);
    }
  };

  const columns = [
    { k: "id", t: "ID" },
    { k: "certificateNumber", t: "Номер аттестата" },
    { k: "name", t: "Название" },
    { k: "issueDate", t: "Дата выдачи" },
    { k: "validityPeriod", t: "Срок действия" },
    { k: "status", t: "Статус" },
    { k: "filial", t: "Филиал" },
    { k: "filialDivision", t: "Районное отделение" },
    { k: "address", t: "Адрес" },
    { k: "isIrrelevant", t: "Неактуальный" },
  ];

  const aoa = [];
  aoa.push(columns.map((c) => ({ v: c.t, s: sHeader })));

  (rows || []).forEach((r) => {
    const st = calcStatus(r);
    const rowStyle = statusFill(st.color);
    const cellStyle = { ...baseCell, ...rowStyle };

    const address = formatAddress(r?.address);

    const rowObj = {
      id: r?.id ?? "",
      certificateNumber: r?.certificateNumber ?? "",
      name: r?.name ?? "",
      issueDate: ddmmyyyy(r?.issueDate),
      validityPeriod: ddmmyyyy(r?.validityPeriod),
      status: st.label,
      filial: r?.filial?.name ?? "",
      filialDivision: r?.filialDivision?.name ?? "",
      address,
      isIrrelevant: r?.isIrrelevant ? "Да" : "Нет",
    };

    aoa.push(
      columns.map((c) => ({
        v: rowObj[c.k],
        s: cellStyle,
      }))
    );
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!rows"] = [{ hpt: 30 }];
  autoWidth(ws);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Отчет");

  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const name =
    ctx?.fileName ||
    `Индикатор_аккредитации_${new Date().toISOString().slice(0, 10)}.xlsx`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

